'use client';

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ChevronDown, ChevronUp, CircleArrowDown, CircleArrowUp, Layers, Pencil, Plus, Sparkles, Star, Trash2, Undo2 } from 'lucide-react';
import { MAX_UNIDADES_ADICIONALES, esUnidadOtra, type ItemAdscripcion } from '@siiv/schemas';
import { SelectInput, type SelectOption } from '@/components/ui/SelectInput';
import formStyles from '@/components/ui/form.module.css';
import inlineStyles from '../../editar/shared/inline-collection/inlineCollection.module.css';
import { InlineMetaLine } from '../../editar/shared/inline-collection/InlineMetaLine';
import { MOTIVO_MAX_LARGO, NOMBRE_OTRA_MAX_LARGO, etiquetaUnidad, type UnidadCatalogoMin } from './adscripcionModel';
import {
	opcionesAgregables,
	puedeAgregarMas,
	type DireccionDeMovimiento,
	type EdicionUnidades,
	type FilaVista,
} from './adscripcionEdicion';
import styles from './adscripcionFilas.module.css';

interface AdscripcionFilasProps {
	filas: FilaVista[];
	edicion: EdicionUnidades;
	catalogo: UnidadCatalogoMin[];
	/** `id -> name` of every unit (the generic "Otra" shows its custom name once typed). */
	nombres: Map<number, string>;
	/** "Otra" is new in the target: its name is required and travels with the request. */
	otraNueva: boolean;
	/** Request mode: every structural change needs its own reason, and the add block asks for the one of the unit being added. */
	pideMotivo: boolean;
	/** The reason typed for each change, keyed by item id. */
	motivos: Record<string, string>;
	/** The change each row asks a reason for (`itemsPorFila`): the field lives in that row and never moves. */
	itemsPorFila: ReadonlyMap<number, ItemAdscripcion>;
	disabled: boolean;
	onMover: (idUnidad: number, direccion: DireccionDeMovimiento) => void;
	onHacerPrincipal: (idUnidad: number) => void;
	onQuitar: (idUnidad: number) => void;
	onRestaurar: (idUnidad: number) => void;
	/** `motivo` is the reason typed in the add block ('' when it was left empty: the new row asks for it). */
	onAgregar: (idUnidad: number, motivo: string) => void;
	/** Only while the researcher has no principal yet. */
	onElegirPrincipal: (idUnidad: number) => void;
	onNombreOtra: (nombre: string) => void;
	onMotivo: (idItem: string, motivo: string) => void;
}

const ID_AGREGAR = 'adscripcion-agregar';
const ID_ELEGIR = 'adscripcion-agregar-unidad';
const ID_MOTIVO_BLOQUE = 'adscripcion-motivo-nuevo';
const ID_NOMBRE_OTRA_NUEVA = 'adscripcion-nombre-otra-nueva';
const ID_ETIQUETA_LISTA = 'adscripcion-unidades-titulo';

/** Id of the reason field of a change (`agregar-12`, `principal`, `quitar-13`). */
const idMotivo = (idItem: string) => `adscripcion-motivo-${idItem}`;

/** What the researcher is asked to explain, worded for the change the field belongs to. */
function placeholderDelMotivo(tipo: ItemAdscripcion['tipo'] | 'agregar_adicional', nueva = false): string {
	switch (tipo) {
		case 'cambiar_principal':
			return nueva ? 'Explica por qué agregas esta unidad como tu unidad principal.' : 'Explica por qué esta pasa a ser tu unidad principal.';
		case 'quitar_adicional':
			return 'Explica por qué quitas esta unidad.';
		default:
			return 'Explica por qué agregas esta unidad.';
	}
}

/** Id of the row's name: the order buttons are named just "Subir"/"Bajar" and point at it to say which unit they move. */
const idNombre = (idUnidad: number) => `adscripcion-nombre-${idUnidad}`;

/** Focuses the first control found among `ids` once the screen re-rendered (the one that had the focus may be gone). */
const enfocar = (...ids: string[]) => {
	if (typeof document === 'undefined') return;
	requestAnimationFrame(() => {
		for (const id of ids) {
			const control = document.getElementById(id);
			if (control) {
				control.focus();
				return;
			}
		}
	});
};

/** A button that moved or vanished takes the focus with it: it goes back to the add control, the next thing to act on. */
const enfocarAgregar = () => enfocar(ID_AGREGAR, ID_ELEGIR);

/**
 * TRIAL (smoke 11): the type and state of a row ("Principal", "Nueva", "Se quita"...) are shown the way the other edit modals
 * show the metadata of an item: small grey text with a lucide icon, parts separated by a dot (see Cargos / Formación).
 * Every part has its own icon: Principal = Star (as the star at the row's left), Adicional = Layers (stacked: one more unit), "Será la principal" =
 * CircleArrowUp, "Era la principal" = CircleArrowDown, Nueva = Sparkles, Se quita = Trash2.
 * To go back to the coloured pill badges set this to `true`: the old markup (`EtiquetasPildora`) and its CSS classes
 * (`.etiquetas`, `.etiqueta*` in `adscripcionFilas.module.css`) are kept untouched.
 */
const ETIQUETAS_COMO_PILDORAS = false as boolean;

interface EtiquetasProps {
	fila: FilaVista;
}

/** The row's type and states as one metadata line: `Adicional · Nueva`, each part with its own icon. */
function EtiquetasMeta({ fila }: EtiquetasProps) {
	// The principal that is leaving is no longer "Adicional": its state ("Era la principal", "Se quita") says what it is.
	const saliendo = fila.quitada && fila.marca === 'era_principal';
	const partes: ReactNode[] = saliendo
		? []
		: [
				<InlineMetaLine key="tipo" icon={fila.principal ? Star : Layers}>
					{fila.principal ? 'Principal' : 'Adicional'}
				</InlineMetaLine>,
			];
	// A principal that is also new to the researcher is an ADDITION as well as the new principal: both facts show (one decision).
	if (fila.marca === 'nueva_principal' && fila.nuevaUnidad) {
		partes.push(
			<InlineMetaLine key="nueva-unidad" icon={Sparkles} className={`${styles.estado} ${styles.estadoNueva}`}>
				Nueva
			</InlineMetaLine>,
		);
	}
	if (fila.marca === 'nueva_principal') {
		partes.push(
			<InlineMetaLine key="nueva-principal" icon={CircleArrowUp} className={`${styles.estado} ${styles.estadoNueva}`}>
				Será la principal
			</InlineMetaLine>,
		);
	}
	if (fila.marca === 'nueva') {
		partes.push(
			<InlineMetaLine key="nueva" icon={Sparkles} className={`${styles.estado} ${styles.estadoNueva}`}>
				Nueva
			</InlineMetaLine>,
		);
	}
	if (fila.marca === 'era_principal') {
		partes.push(
			<InlineMetaLine key="era-principal" icon={CircleArrowDown} className={`${styles.estado} ${styles.estadoEra}`}>
				Era la principal
			</InlineMetaLine>,
		);
	}
	if (fila.quitada) {
		partes.push(
			<InlineMetaLine key="quita" icon={Trash2} className={`${styles.estado} ${styles.estadoQuita}`}>
				Se quita
			</InlineMetaLine>,
		);
	}
	return (
		<p className={`${inlineStyles.inlineListMeta} ${styles.meta}`}>
			{partes.flatMap((parte, indice) =>
				indice === 0
					? [parte]
					: [
							<span key={`separador-${indice}`} className={styles.separador} aria-hidden="true">
								·
							</span>,
							parte,
						],
			)}
		</p>
	);
}

/** The previous look: one coloured pill per type / state. Only rendered while `ETIQUETAS_COMO_PILDORAS` is on. */
function EtiquetasPildora({ fila }: EtiquetasProps) {
	return (
		<p className={styles.etiquetas}>
			<span className={`${styles.etiqueta} ${fila.principal ? styles.etiquetaPrincipal : ''}`}>
				{fila.principal ? 'Principal' : 'Adicional'}
			</span>
			{fila.marca === 'nueva_principal' ? (
				<span className={`${styles.etiqueta} ${styles.etiquetaNueva}`}>Será la principal</span>
			) : null}
			{fila.marca === 'nueva' ? <span className={`${styles.etiqueta} ${styles.etiquetaNueva}`}>Nueva</span> : null}
			{fila.marca === 'era_principal' ? <span className={`${styles.etiqueta} ${styles.etiquetaEra}`}>Era la principal</span> : null}
			{fila.quitada ? <span className={`${styles.etiqueta} ${styles.etiquetaQuita}`}>Se quita</span> : null}
		</p>
	);
}

const Etiquetas = ETIQUETAS_COMO_PILDORAS ? EtiquetasPildora : EtiquetasMeta;

interface CampoMotivoProps {
	id: string;
	/** Which change the reason is for, read by screen readers after the label ("Motivo del cambio en «X»"). */
	cambio: string;
	value: string;
	placeholder: string;
	onChange: (motivo: string) => void;
	disabled: boolean;
}

/** The reason of ONE change. It is written where that change is (its row, or the add block) and never moves. */
function CampoMotivo({ id, cambio, value, placeholder, onChange, disabled }: CampoMotivoProps) {
	return (
		<div className={formStyles.modalField}>
			<label className={formStyles.modalLabel} htmlFor={id}>
				<span>
					Motivo<span className="sr-only"> del cambio en {cambio}</span>
				</span>
				<span className={styles.obligatorio}>Obligatorio</span>
			</label>
			<textarea
				id={id}
				value={value}
				onChange={(event) => onChange(event.target.value)}
				rows={2}
				maxLength={MOTIVO_MAX_LARGO}
				disabled={disabled}
				aria-required="true"
				placeholder={placeholder}
				className={`${formStyles.modalInput} ${styles.motivoTexto}`}
			/>
		</div>
	);
}

/**
 * One row per unit of the adscription modal: the principal first, then the additional units in the staged order.
 * Presentational: every rule (what a row may do, what the staged state means) lives in `adscripcionEdicion`; the
 * container owns the state and the network. The row look and its buttons are the ones of the other edit modals, and so is
 * the way to add: a dashed "+ Agregar unidad" row that opens into a block (unit, then the reason of the request, then
 * "Agregar" / "Cancelar"). Every structural change has its own reason, written in the row of that change (the add block asks for the one
 * of the unit being added, and from then on it is edited in the unit's row): the field never jumps from row to row.
 */
export function AdscripcionFilas({
	filas,
	edicion,
	catalogo,
	nombres,
	otraNueva,
	pideMotivo,
	motivos,
	itemsPorFila,
	disabled,
	onMover,
	onHacerPrincipal,
	onQuitar,
	onRestaurar,
	onAgregar,
	onElegirPrincipal,
	onNombreOtra,
	onMotivo,
}: AdscripcionFilasProps) {
	// The saved "Otra" name is edited in place: "Editar nombre" opens it, "Guardar" keeps the text, "Cancelar" puts back
	// the name it had when it was opened (`null` = not being edited).
	const [nombreAntesDeEditar, setNombreAntesDeEditar] = useState<string | null>(null);
	const editandoOtra = nombreAntesDeEditar !== null;
	const abrirNombreOtra = () => setNombreAntesDeEditar(edicion.nombreOtra);
	const guardarNombreOtra = () => setNombreAntesDeEditar(null);
	const cancelarNombreOtra = () => {
		if (nombreAntesDeEditar !== null) onNombreOtra(nombreAntesDeEditar);
		setNombreAntesDeEditar(null);
	};
	const [movida, setMovida] = useState<number | null>(null);
	// The add block: closed it is the dashed "Agregar unidad" row; open it is a form inside the list.
	const [agregando, setAgregando] = useState(false);
	const [elegida, setElegida] = useState('');
	// The reason of the unit being added, typed in the block; it moves to the unit's row when it is added.
	const [motivoBloque, setMotivoBloque] = useState('');
	const lista = useRef<HTMLUListElement>(null);

	const nombreDe = (idUnidad: number) => nombres.get(idUnidad) ?? `Unidad ${idUnidad}`;
	const opciones = useMemo<SelectOption[]>(
		() => opcionesAgregables(catalogo, edicion).map((unidad) => ({ value: String(unidad.idUnidad), label: etiquetaUnidad(unidad, unidad.idUnidad) })),
		[catalogo, edicion],
	);
	const todas = useMemo<SelectOption[]>(
		() => catalogo.map((unidad) => ({ value: String(unidad.idUnidad), label: etiquetaUnidad(unidad, unidad.idUnidad) })),
		[catalogo],
	);

	const sinPrincipal = edicion.idPrincipal === 0;
	const adicionales = filas.filter((fila) => !fila.principal);
	const activas = adicionales.filter((fila) => !fila.quitada).length;
	const hayLugar = puedeAgregarMas(edicion);

	// A choice the list no longer offers (the unit became the principal meanwhile) counts as no choice.
	const eleccion = opciones.some((opcion) => opcion.value === elegida) ? Number(elegida) : null;
	// The block asks for the reason of the unit it adds as soon as one is chosen; once added, its row asks for it.
	const motivoEnBloque = pideMotivo && agregando && eleccion !== null;
	// "Otra" is named while it is being added, next to the reason: the row lets the name be changed afterwards.
	const otraEnBloque = agregando && eleccion !== null && esUnidadOtra(eleccion);
	const faltaNombreOtra = otraEnBloque && edicion.nombreOtra.trim() === '';

	useEffect(() => {
		if (agregando) document.getElementById(ID_ELEGIR)?.focus();
	}, [agregando]);

	const abrirBloque = () => {
		setElegida('');
		setMotivoBloque('');
		setAgregando(true);
	};
	const cerrarBloque = () => {
		if (otraEnBloque) onNombreOtra('');
		setAgregando(false);
		setElegida('');
		setMotivoBloque('');
		enfocar(ID_AGREGAR);
	};
	const confirmarAgregar = () => {
		if (eleccion === null || !hayLugar || faltaNombreOtra) return;
		// The reason typed in the block travels with the unit. If it is still empty, the new row asks for it and the focus goes there
		// (it is the next thing to do).
		const faltaMotivo = pideMotivo && motivoBloque.trim() === '';
		onAgregar(eleccion, pideMotivo ? motivoBloque : '');
		setAgregando(false);
		setElegida('');
		setMotivoBloque('');
		enfocar(...(faltaMotivo ? [idMotivo(`agregar-${eleccion}`), ID_AGREGAR] : [ID_AGREGAR]));
	};

	/** After a move the row re-renders in its new place; the button that moved it keeps the focus (or its opposite at an edge). */
	const mover = (fila: FilaVista, direccion: DireccionDeMovimiento) => {
		onMover(fila.idUnidad, direccion);
		setMovida(fila.idUnidad);
		requestAnimationFrame(() => {
			const opuesta = direccion === 'subir' ? 'bajar' : 'subir';
			for (const candidata of [direccion, opuesta]) {
				const boton = lista.current?.querySelector<HTMLButtonElement>(`[data-orden="${fila.idUnidad}-${candidata}"]`);
				if (boton && !boton.disabled) {
					boton.focus();
					return;
				}
			}
		});
	};

	const anuncio = (() => {
		const fila = adicionales.find((candidata) => candidata.idUnidad === movida);
		return fila ? `${nombreDe(fila.idUnidad)}: unidad adicional ${fila.posicion} de ${adicionales.length}.` : '';
	})();

	return (
		<div className={formStyles.modalField}>
			<div className={formStyles.modalLabel}>
				<span id={ID_ETIQUETA_LISTA}>Unidades</span>
				<span
					className={`${styles.contador} ${activas > MAX_UNIDADES_ADICIONALES ? styles.contadorExcedido : ''}`}
					aria-live="polite"
				>
					{activas} de {MAX_UNIDADES_ADICIONALES} adicionales
				</span>
			</div>

			<ul ref={lista} className={styles.lista} aria-labelledby={ID_ETIQUETA_LISTA}>
				{sinPrincipal ? (
					<li className={`${inlineStyles.inlineListItem} ${styles.fila} ${styles.filaExpandida}`}>
						<div className={inlineStyles.rowPositionLabel} aria-hidden="true">
							<Star size={16} />
						</div>
						<div className={inlineStyles.inlineListContent}>
							<p className={inlineStyles.inlineListTitle}>Sin unidad principal</p>
							<SelectInput
								id="adscripcion-principal"
								aria-label="Elegir la unidad principal"
								value=""
								onChange={(valor) => onElegirPrincipal(Number(valor))}
								options={todas}
								disabled={disabled}
								searchable
								placeholder="— Elegir unidad principal —"
							/>
						</div>
					</li>
				) : null}

				{filas.map((fila) => {
					const nombre = nombreDe(fila.idUnidad);
					const apagada = fila.quitada;
						// The former principal, removed: it departs unless the researcher keeps it, so the way back reads "keep it".
						const mantiene = fila.quitada && fila.marca === 'era_principal';
					const mostrarNombreOtra = fila.esOtra && !fila.quitada && (otraNueva || editandoOtra);
					const editandoNombreAqui = fila.esOtra && !fila.quitada && !otraNueva && editandoOtra;
					// The change this row is the home of: its field lives here for as long as the change is staged.
					const itemMotivo = itemsPorFila.get(fila.idUnidad);
					const pideMotivoAqui = itemMotivo !== undefined;
					// Collapsed rows all have the same fixed height; only a row that opens a field (reason, "Otra" name) may grow.
					const expandida = pideMotivoAqui || mostrarNombreOtra;

					return (
						<li
							key={fila.idUnidad}
							className={`${inlineStyles.inlineListItem} ${styles.fila} ${apagada ? inlineStyles.inlineListItemMuted : ''} ${pideMotivoAqui ? styles.filaConMotivo : ''} ${expandida ? styles.filaExpandida : ''} ${editandoNombreAqui ? `${inlineStyles.inlineListItemEditing} ${styles.filaEditandoNombre}` : ''}`}
							data-unidad={fila.idUnidad}
						>
							{fila.principal ? (
								<div className={inlineStyles.rowPositionLabel} title="Unidad principal">
									<Star size={16} aria-hidden="true" />
								</div>
							) : (
								<div className={styles.orden}>
									<button
										type="button"
										className={inlineStyles.reorderMoveBtn}
										data-orden={`${fila.idUnidad}-subir`}
										onClick={() => mover(fila, 'subir')}
										disabled={disabled || !fila.puedeSubir}
										aria-label="Subir"
										aria-describedby={idNombre(fila.idUnidad)}
									>
										<ChevronUp size={15} aria-hidden="true" />
									</button>
									<span className={inlineStyles.reorderIndex} aria-hidden="true">#{fila.posicion}</span>
									<button
										type="button"
										className={inlineStyles.reorderMoveBtn}
										data-orden={`${fila.idUnidad}-bajar`}
										onClick={() => mover(fila, 'bajar')}
										disabled={disabled || !fila.puedeBajar}
										aria-label="Bajar"
										aria-describedby={idNombre(fila.idUnidad)}
									>
										<ChevronDown size={15} aria-hidden="true" />
									</button>
								</div>
							)}

							<div className={inlineStyles.inlineListContent}>
								<p
										id={idNombre(fila.idUnidad)}
										className={`${inlineStyles.inlineListTitle} ${styles.nombre} ${fila.quitada ? styles.tachado : ''}`}
										title={nombre}
									>
									{nombre}
								</p>
								{/* While the saved name is being edited the input takes the place of the row's tags. */}
								{editandoNombreAqui ? null : <Etiquetas fila={fila} />}
								{mostrarNombreOtra ? (
									<div className={`${styles.nombreOtra} ${editandoNombreAqui ? styles.nombreOtraSolo : ''}`}>
										<div className={styles.nombreOtraFila}>
										<input
											className={formStyles.modalInput}
											type="text"
											value={edicion.nombreOtra}
											maxLength={NOMBRE_OTRA_MAX_LARGO}
											onChange={(event) => onNombreOtra(event.target.value)}
											disabled={disabled}
											aria-label="Nombre de la unidad «Otra»"
											aria-required={otraNueva}
											placeholder={otraNueva ? 'Escribe el nombre de la unidad' : 'Déjalo vacío para conservar el nombre actual'}
										/>
										{otraNueva ? null : (
											<>
												<button
													type="button"
													className={`${inlineStyles.actionBtn} ${inlineStyles.actionSaveBtn} ${styles.nombreOtraBtn}`}
													onClick={guardarNombreOtra}
													disabled={disabled}
												>
													Guardar
												</button>
												<button
													type="button"
													className={`${inlineStyles.actionBtn} ${styles.nombreOtraBtn}`}
													onClick={cancelarNombreOtra}
													disabled={disabled}
												>
													Cancelar
												</button>
											</>
										)}
										</div>
										<p className={styles.ayuda}>
											{otraNueva
												? 'Obligatorio: es el nombre que se mostrará en tu perfil una vez aprobada.'
												: 'El nombre se aplica de inmediato al guardar.'}
										</p>
									</div>
								) : null}
							</div>

							<div className={`${inlineStyles.inlineListActions} ${styles.acciones}`}>
								{fila.esOtra && !fila.quitada && !otraNueva && !editandoOtra ? (
									<button
										type="button"
										className={inlineStyles.actionBtn}
										onClick={abrirNombreOtra}
										disabled={disabled}
										aria-label={`Editar el nombre de la unidad ${nombre}`}
									>
										<Pencil size={14} aria-hidden="true" />
										<span>Editar nombre</span>
									</button>
								) : null}
								{fila.puedeHacerPrincipal ? (
									<button
										type="button"
										className={inlineStyles.actionBtn}
										onClick={() => onHacerPrincipal(fila.idUnidad)}
										disabled={disabled}
										aria-label={`Hacer de ${nombre} la unidad principal`}
									>
										<Star size={14} aria-hidden="true" />
										<span>Hacer principal</span>
									</button>
								) : null}
								{fila.puedeQuitar ? (
									<button
										type="button"
										className={`${inlineStyles.actionBtn} ${inlineStyles.actionDangerBtn}`}
										onClick={() => {
											onQuitar(fila.idUnidad);
											// A new unit leaves the list (nothing to restore), so its button vanishes with it.
												if (fila.marca === 'nueva') enfocarAgregar();
										}}
										disabled={disabled}
										aria-label={`Quitar la unidad ${nombre}`}
									>
										<Trash2 size={14} aria-hidden="true" />
										<span>Quitar</span>
									</button>
								) : null}
								{fila.puedeRestaurar ? (
									<button
										type="button"
										className={inlineStyles.actionBtn}
										onClick={() => onRestaurar(fila.idUnidad)}
										disabled={disabled || !hayLugar}
										title={
												hayLugar
													? undefined
													: `Ya hay ${MAX_UNIDADES_ADICIONALES} unidades adicionales: quita otra para ${mantiene ? 'mantener' : 'restaurar'} esta.`
											}
										aria-label={mantiene ? `Mantener la unidad ${nombre} como unidad adicional` : `Restaurar la unidad ${nombre}`}
									>
										<Undo2 size={14} aria-hidden="true" />
										<span>{mantiene ? 'Mantener como adicional' : 'Restaurar'}</span>
									</button>
								) : null}
							</div>

							{itemMotivo ? (
								<div className={styles.motivoFila}>
									<CampoMotivo
										id={idMotivo(itemMotivo.id)}
										cambio={`«${nombre}»`}
										value={motivos[itemMotivo.id] ?? ''}
										placeholder={placeholderDelMotivo(itemMotivo.tipo, itemMotivo.tipo === 'cambiar_principal' && itemMotivo.nueva === true)}
										onChange={(texto) => onMotivo(itemMotivo.id, texto)}
										disabled={disabled}
									/>
								</div>
							) : null}
						</li>
					);
				})}

				<li>
					{agregando ? (
						<div
							className={`${inlineStyles.inlineListItem} ${inlineStyles.inlineListItemEditing} ${inlineStyles.inlineListItemCreating} ${styles.bloque}`}
							role="group"
							aria-label="Agregar una unidad adicional"
						>
							<div className={styles.bloqueCampos}>
								<div className={formStyles.modalField}>
									<label className={formStyles.modalLabel} htmlFor={ID_ELEGIR}>
										Unidad
									</label>
									<SelectInput
										id={ID_ELEGIR}
										value={elegida}
										onChange={setElegida}
										options={opciones}
										disabled={disabled}
										searchable
										placeholder="— Elegir unidad —"
									/>
									{hayLugar ? null : (
										<p className={styles.ayuda}>
											Ya hay {MAX_UNIDADES_ADICIONALES} unidades adicionales: quita una para agregar otra.
										</p>
									)}
								</div>
								{otraEnBloque ? (
									<div className={formStyles.modalField}>
										<label className={formStyles.modalLabel} htmlFor={ID_NOMBRE_OTRA_NUEVA}>
											<span>Nombre de la unidad</span>
											<span className={styles.obligatorio}>Obligatorio</span>
										</label>
										<input
											id={ID_NOMBRE_OTRA_NUEVA}
											className={formStyles.modalInput}
											type="text"
											value={edicion.nombreOtra}
											maxLength={NOMBRE_OTRA_MAX_LARGO}
											onChange={(event) => onNombreOtra(event.target.value)}
											disabled={disabled}
											aria-required="true"
											placeholder="Escribe el nombre de la unidad"
										/>
										<p className={styles.ayuda}>Es el nombre que se mostrará en tu perfil una vez aprobada.</p>
									</div>
								) : null}
								{motivoEnBloque ? (
									<CampoMotivo
										id={ID_MOTIVO_BLOQUE}
										cambio="la unidad que se agrega"
										value={motivoBloque}
										placeholder={placeholderDelMotivo('agregar_adicional')}
										onChange={setMotivoBloque}
										disabled={disabled}
									/>
								) : null}
							</div>
							<div className={`${inlineStyles.inlineListActions} ${styles.bloqueAcciones}`}>
								<button
									type="button"
									className={`${inlineStyles.actionBtn} ${inlineStyles.actionSaveBtn}`}
									onClick={confirmarAgregar}
									disabled={disabled || eleccion === null || !hayLugar || faltaNombreOtra}
									title={faltaNombreOtra ? 'Escribe el nombre de la unidad para agregarla.' : undefined}
								>
									Agregar
								</button>
								<button type="button" className={inlineStyles.actionBtn} onClick={cerrarBloque} disabled={disabled}>
									Cancelar
								</button>
							</div>
						</div>
					) : (
						<button
							type="button"
							id={ID_AGREGAR}
							className={`${inlineStyles.inlineListItem} ${inlineStyles.addInlineRowBtn} ${styles.filaAgregar}`}
							onClick={abrirBloque}
							disabled={disabled || sinPrincipal || !hayLugar}
						>
							<span className={inlineStyles.addInlineRowIcon}>
								<Plus size={16} aria-hidden="true" />
							</span>
							<span>
								{sinPrincipal
									? 'Elige primero la unidad principal'
									: hayLugar
										? 'Agregar unidad'
										: `Máximo ${MAX_UNIDADES_ADICIONALES} unidades adicionales: quita una para agregar otra`}
							</span>
						</button>
					)}
				</li>
			</ul>

			<p className="sr-only" role="status">
				{anuncio}
			</p>
		</div>
	);
}
